import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import screenAndStyle from '../screen-and-style';
import { usePaginationRange, PAGINATION_BREAK } from './use-pagination-range';

// Max number of siblings either side of current page
// * First & Last page always displayed
// * Prev & Next displayed when valid
// [1, PAGINATION_BREAK, 9, _10_, 11, PAGINATION_BREAK, 15] = 1 siblings
// [1, PAGINATION_BREAK, 8, 9, _10_, 11, 12, PAGINATION_BREAK, 15] = 2 siblings
const MOBILE_PAGINATION_SIBLINGS = 1;
const DESKTOP_PAGINATION_SIBLINGS = 4;
// ---------}

const scrollTo = (target) => {
  var top = target || 0,
    $target;

  if (isNaN(top)) {
    $target = $(target || 'body');

    if (['auto', 'scroll'].indexOf($target.css('overflow-y')) > -1) {
      // we're in a scrolling div
      $target.animate({ scrollTop: 0 });
      return;
    }

    top = $target.offset().top;
  }

  $('html, body').animate({
    scrollTop: top,
  });
};

const Paginator = ({
  scrollTarget,
  page: currentPage,
  onPagePrevious,
  onPageNext,
  totalPageCount,
  onGoToPage,
  title,
  children,
}) => {
  const [isClient, setIsClient] = React.useState(false);

  React.useEffect(() => {
    setIsClient(true);
  }, []);

  const handlePagePrevious = useCallback(
    (e) => {
      e.preventDefault();
      if (currentPage > 1) {
        scrollTo(scrollTarget);
        onPagePrevious();
      }
    },
    [scrollTarget, onPagePrevious, currentPage],
  );

  const handlePageNext = useCallback(
    (e) => {
      e.preventDefault();
      if (currentPage < totalPageCount) {
        scrollTo(scrollTarget);
        onPageNext();
      }
    },
    [scrollTarget, onPageNext, currentPage, totalPageCount],
  );

  const maxSiblings = screenAndStyle.assumePhoneOrSmallScreen()
    ? MOBILE_PAGINATION_SIBLINGS
    : DESKTOP_PAGINATION_SIBLINGS;
  const { paginationRange } = usePaginationRange({
    totalPageCount,
    maxSiblings,
    currentPage,
  });

  return (
    <>
      {children}
      {totalPageCount > 1 && (
        <nav
          role="navigation"
          aria-label={title}
          className="paginator text-center">
          <ul className="pagination justify-content-center">
            <div
              className={classNames(
                'page-item',
                currentPage <= 1 && 'disabled',
              )}>
              <a
                className="page-link prev"
                aria-label="Previous"
                rel="prev"
                href="#"
                aria-disabled={currentPage <= 1}
                tabIndex={currentPage <= 1 ? '-1' : '0'}
                onClick={handlePagePrevious}>
                <i className="fa fa-chevron-left" aria-hidden="true" />
                <span className="sr-only">Previous</span>
              </a>
            </div>
            {isClient &&
              paginationRange.map((pageNumber, index) => {
                if (pageNumber === PAGINATION_BREAK) {
                  return (
                    <li
                      key={`br-${index}`}
                      className="page-item page-item-break">
                      &hellip;
                    </li>
                  );
                }

                const selected = pageNumber == currentPage;
                const goToPage = (e) => {
                  e.preventDefault();
                  scrollTo(scrollTarget);
                  onGoToPage(pageNumber);
                };

                return (
                  <li
                    className={classNames('page-item', {
                      active: selected,
                    })}
                    key={pageNumber}>
                    <a
                      className="page-link"
                      onClick={goToPage}
                      href="#"
                      aria-label={`Page ${pageNumber}${selected ? ' is your current page' : ''}`}
                      tabIndex={selected ? '-1' : '0'}
                      aria-current={selected ? 'page' : null}>
                      {pageNumber}
                    </a>
                  </li>
                );
              })}
            <div
              className={classNames(
                'page-item',
                currentPage >= totalPageCount && 'disabled',
              )}>
              <a
                className="page-link next"
                aria-label="Next"
                rel="next"
                href="#"
                aria-disabled={currentPage >= totalPageCount}
                tabIndex={currentPage >= totalPageCount ? '-1' : '0'}
                onClick={handlePageNext}>
                <span className="sr-only">Next</span>
                <i className="fa fa-chevron-right" aria-hidden="true" />
              </a>
            </div>
          </ul>
        </nav>
      )}
    </>
  );
};

Paginator.propTypes = {
  scrollTarget: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
    .isRequired,
  page: PropTypes.number, // aka currentPage
  totalPageCount: PropTypes.number.isRequired,
  onPagePrevious: PropTypes.func.isRequired,
  onPageNext: PropTypes.func.isRequired,
  onGoToPage: PropTypes.func.isRequired,
  children: PropTypes.oneOfType([
    PropTypes.node,
    PropTypes.arrayOf(PropTypes.node),
  ]),
  title: PropTypes.string,
};

Paginator.defaultProps = {
  title: 'Pagination Navigation',
};

export default Paginator;
